import { alpha, Theme } from '@mui/material/styles';

export type GlassStrength = 'subtle' | 'regular' | 'strong';

const opacityByStrength = {
  light: { subtle: 0.07, regular: 0.1, strong: 0.24 },
  dark: { subtle: 0.06, regular: 0.08, strong: 0.2 },
} as const;

const fabricVeilOpacity = {
  light: { start: 0.12, end: 0.08 },
  dark: { start: 0.06, end: 0.04 },
} as const;

/** Opaque, high-contrast print treatment shared by every glass-backed surface. */
export const glassPrintSurface = (theme: Theme) => ({
  backgroundColor: theme.palette.common.white,
  backgroundImage: 'none',
  color: theme.palette.grey[900],
  borderColor: theme.palette.grey[300],
  boxShadow: 'none',
  backdropFilter: 'none',
  WebkitBackdropFilter: 'none',
  '& .MuiTypography-root, & .MuiFormLabel-root, & .MuiInputBase-input, & .MuiTableCell-root': {
    color: 'inherit',
  },
  '& a, & .MuiLink-root': {
    color: theme.palette.primary.darker,
    textDecoration: 'underline',
    textDecorationColor: 'currentColor',
  },
  '& a .MuiTypography-root, & .MuiLink-root .MuiTypography-root': {
    color: 'inherit',
  },
});

/** Shared glass treatment for cards, panels, dialogs, and navigation. */
export const glassSurface = (theme: Theme, strength: GlassStrength = 'regular') => {
  const mode = theme.palette.mode === 'light' ? 'light' : 'dark';
  const isLight = mode === 'light';
  const opacity = opacityByStrength[mode][strength];
  const base = isLight ? '#F8FBFF' : '#0A1426';
  const edge = alpha(isLight ? theme.palette.primary.main : theme.palette.info.light, isLight ? 0.22 : 0.24);
  const highlight = alpha(theme.palette.common.white, isLight ? 0.2 : 0.035);
  const shadow = alpha(isLight ? theme.palette.grey[700] : theme.palette.common.black, isLight ? 0.14 : 0.34);

  return {
    backgroundColor: alpha(base, opacity),
    backgroundImage: `linear-gradient(145deg, ${highlight} 0%, ${alpha(base, Math.max(0.04, opacity - 0.06))} 48%, ${alpha(
      isLight ? '#EAF3FF' : '#07101F',
      opacity
    )} 100%)`,
    border: `1px solid ${edge}`,
    boxShadow: `inset 0 1px 0 ${highlight}, 0 18px 42px -28px ${shadow}`,
    backdropFilter: 'blur(18px) saturate(135%)',
    WebkitBackdropFilter: 'blur(18px) saturate(135%)',
    backgroundClip: 'padding-box',
    '@media (max-width: 600px)': {
      backdropFilter: 'blur(11px) saturate(120%)',
      WebkitBackdropFilter: 'blur(11px) saturate(120%)',
    },
    '@media (prefers-reduced-transparency: reduce)': {
      backgroundColor: alpha(base, 0.96),
      backdropFilter: 'none',
      WebkitBackdropFilter: 'none',
    },
    '@media print': {
      ...glassPrintSurface(theme),
    },
  };
};

/** A restrained tinted header that lets the canvas continue through compact dashboard tiles. */
export const dashboardTileHeaderGlass = (theme: Theme) => {
  const isLight = theme.palette.mode === 'light';

  return {
    backgroundColor: alpha(theme.palette.primary.main, isLight ? 0.08 : 0.09),
    backgroundImage: `linear-gradient(120deg, ${alpha(theme.palette.primary.main, isLight ? 0.12 : 0.14)}, ${alpha(
      theme.palette.info.dark,
      isLight ? 0.07 : 0.08
    )})`,
    borderBottom: `1px solid ${alpha(theme.palette.primary.main, isLight ? 0.24 : 0.32)}`,
    backdropFilter: 'blur(14px) saturate(125%)',
    WebkitBackdropFilter: 'blur(14px) saturate(125%)',
    color: theme.palette.text.primary,
    '@media (prefers-reduced-transparency: reduce)': {
      backgroundColor: isLight ? theme.palette.primary.lighter : '#0A1426',
      backgroundImage: 'none',
      backdropFilter: 'none',
      WebkitBackdropFilter: 'none',
    },
    '@media print': {
      ...glassPrintSurface(theme),
    },
  };
};

/** The page-level veil keeps text readable while allowing the particle fabric to show through. */
export const glassFabricVeil = (theme: Theme) => {
  const mode = theme.palette.mode === 'light' ? 'light' : 'dark';
  const opacity = fabricVeilOpacity[mode];

  return `linear-gradient(${alpha(theme.palette.background.default, opacity.start)}, ${alpha(
    theme.palette.background.default,
    opacity.end
  )})`;
};

export const glassInteractive = (theme: Theme) => ({
  transition: theme.transitions.create(['transform', 'box-shadow', 'border-color', 'background-color'], {
    duration: theme.transitions.duration.shorter,
  }),
  '@media (hover: hover) and (pointer: fine)': {
    '&:hover': {
      transform: 'translateY(-2px)',
      borderColor: alpha(theme.palette.info.main, theme.palette.mode === 'light' ? 0.34 : 0.42),
      boxShadow: `inset 0 1px 0 ${alpha(theme.palette.common.white, theme.palette.mode === 'light' ? 0.82 : 0.12)}, 0 22px 46px -26px ${alpha(
        theme.palette.mode === 'light' ? theme.palette.grey[700] : theme.palette.common.black,
        theme.palette.mode === 'light' ? 0.2 : 0.46
      )}`,
    },
  },
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
    '&:hover': { transform: 'none' },
  },
});
